import React, { useState, useRef, useEffect } from 'react';
import { Input } from './ui/input';
import { ChevronDown } from 'lucide-react';

const COUNTRY_CODES = [
  { code: '+91', country: 'IN', name: 'India', flag: '🇮🇳' },
  { code: '+1', country: 'US', name: 'United States', flag: '🇺🇸' },
  { code: '+44', country: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
  { code: '+61', country: 'AU', name: 'Australia', flag: '🇦🇺' },
  { code: '+49', country: 'DE', name: 'Germany', flag: '🇩🇪' },
  { code: '+33', country: 'FR', name: 'France', flag: '🇫🇷' },
  { code: '+81', country: 'JP', name: 'Japan', flag: '🇯🇵' },
  { code: '+86', country: 'CN', name: 'China', flag: '🇨🇳' },
  { code: '+55', country: 'BR', name: 'Brazil', flag: '🇧🇷' },
  { code: '+52', country: 'MX', name: 'Mexico', flag: '🇲🇽' },
  { code: '+1', country: 'CA', name: 'Canada', flag: '🇨🇦' },
  { code: '+82', country: 'KR', name: 'South Korea', flag: '🇰🇷' },
  { code: '+39', country: 'IT', name: 'Italy', flag: '🇮🇹' },
  { code: '+34', country: 'ES', name: 'Spain', flag: '🇪🇸' },
  { code: '+31', country: 'NL', name: 'Netherlands', flag: '🇳🇱' },
  { code: '+46', country: 'SE', name: 'Sweden', flag: '🇸🇪' },
  { code: '+65', country: 'SG', name: 'Singapore', flag: '🇸🇬' },
  { code: '+971', country: 'AE', name: 'UAE', flag: '🇦🇪' },
  { code: '+972', country: 'IL', name: 'Israel', flag: '🇮🇱' },
  { code: '+27', country: 'ZA', name: 'South Africa', flag: '🇿🇦' },
  { code: '+64', country: 'NZ', name: 'New Zealand', flag: '🇳🇿' },
  { code: '+353', country: 'IE', name: 'Ireland', flag: '🇮🇪' },
  { code: '+41', country: 'CH', name: 'Switzerland', flag: '🇨🇭' },
  { code: '+351', country: 'PT', name: 'Portugal', flag: '🇵🇹' },
  { code: '+48', country: 'PL', name: 'Poland', flag: '🇵🇱' },
  { code: '+54', country: 'AR', name: 'Argentina', flag: '🇦🇷' },
  { code: '+57', country: 'CO', name: 'Colombia', flag: '🇨🇴' },
  { code: '+62', country: 'ID', name: 'Indonesia', flag: '🇮🇩' },
  { code: '+66', country: 'TH', name: 'Thailand', flag: '🇹🇭' },
  { code: '+63', country: 'PH', name: 'Philippines', flag: '🇵🇭' },
  { code: '+90', country: 'TR', name: 'Turkey', flag: '🇹🇷' },
  { code: '+966', country: 'SA', name: 'Saudi Arabia', flag: '🇸🇦' },
  { code: '+234', country: 'NG', name: 'Nigeria', flag: '🇳🇬' },
  { code: '+20', country: 'EG', name: 'Egypt', flag: '🇪🇬' },
  { code: '+254', country: 'KE', name: 'Kenya', flag: '🇰🇪' },
  { code: '+7', country: 'RU', name: 'Russia', flag: '🇷🇺' },
  { code: '+380', country: 'UA', name: 'Ukraine', flag: '🇺🇦' },
  { code: '+84', country: 'VN', name: 'Vietnam', flag: '🇻🇳' },
  { code: '+60', country: 'MY', name: 'Malaysia', flag: '🇲🇾' },
  { code: '+886', country: 'TW', name: 'Taiwan', flag: '🇹🇼' },
  { code: '+852', country: 'HK', name: 'Hong Kong', flag: '🇭🇰' },
  { code: '+45', country: 'DK', name: 'Denmark', flag: '🇩🇰' },
  { code: '+47', country: 'NO', name: 'Norway', flag: '🇳🇴' },
  { code: '+358', country: 'FI', name: 'Finland', flag: '🇫🇮' },
  { code: '+43', country: 'AT', name: 'Austria', flag: '🇦🇹' },
  { code: '+32', country: 'BE', name: 'Belgium', flag: '🇧🇪' },
  { code: '+92', country: 'PK', name: 'Pakistan', flag: '🇵🇰' },
  { code: '+880', country: 'BD', name: 'Bangladesh', flag: '🇧🇩' },
  { code: '+94', country: 'LK', name: 'Sri Lanka', flag: '🇱🇰' },
  { code: '+977', country: 'NP', name: 'Nepal', flag: '🇳🇵' },
];

// Map dial code → country entry for detecting from existing phone
const DIAL_CODE_MAP = {};
COUNTRY_CODES.forEach(c => { if (!DIAL_CODE_MAP[c.code]) DIAL_CODE_MAP[c.code] = c; });

function detectCountryFromPhone(phone) {
  if (!phone || !phone.startsWith('+')) return null;
  // Try longest codes first (3 digits, then 2, then 1)
  for (const len of [4, 3, 2]) {
    const prefix = phone.substring(0, len);
    if (DIAL_CODE_MAP[prefix]) return DIAL_CODE_MAP[prefix];
  }
  return null;
}

export function PhoneInput({ value, onChange, defaultCountryCode = '+91', className = '' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Parse the current value to extract country code and local number
  const getSelectedAndLocal = () => {
    const def = COUNTRY_CODES.find(c => c.code === defaultCountryCode) || COUNTRY_CODES[0];
    if (!value) return { selected: def, local: '' };

    // Explicit international format (starts with '+') — detect & strip the dial code
    if (value.startsWith('+')) {
      const detected = detectCountryFromPhone(value);
      if (detected) {
        return { selected: detected, local: value.substring(detected.code.length).replace(/[^\d]/g, '') };
      }
      // Unknown code — keep digits as local under default country
      return { selected: def, local: value.replace(/[^\d]/g, '') };
    }

    // No '+' prefix — treat the value as digits. Only strip the default dial code
    // when the number clearly includes it (e.g. 91 + 10-digit national = 12 digits).
    // Do NOT blindly strip leading digits, otherwise a plain 10-digit number like
    // 9920547379 gets mangled to 0547379.
    const digits = value.replace(/[^\d]/g, '');
    const defDigits = def.code.replace('+', '');
    if (digits.startsWith(defDigits) && (digits.length - defDigits.length) === 10) {
      return { selected: def, local: digits.substring(defDigits.length) };
    }
    return { selected: def, local: digits };
  };

  const { selected, local } = getSelectedAndLocal();

  const handleCountrySelect = (country) => {
    setIsOpen(false);
    setSearch('');
    const newPhone = local ? `${country.code}${local}` : '';
    onChange(newPhone, country);
  };

  const handleLocalChange = (e) => {
    let digits = e.target.value.replace(/[^\d]/g, '');
    // Symmetry with getSelectedAndLocal: if the user pastes/types the number WITH
    // the selected country's dial code prefixed (e.g. 91 + 10-digit = 12 digits),
    // strip it so we don't double-prepend and store +91919821231355.
    const dial = selected.code.replace('+', '');
    if (digits.startsWith(dial) && (digits.length - dial.length) === 10) {
      digits = digits.substring(dial.length);
    }
    const newPhone = digits ? `${selected.code}${digits}` : '';
    onChange(newPhone, selected);
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = search
    ? COUNTRY_CODES.filter(c =>
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.code.includes(search) ||
        c.country.toLowerCase().includes(search.toLowerCase())
      )
    : COUNTRY_CODES;

  return (
    <div className={`relative flex items-center ${className}`} ref={dropdownRef}>
      {/* Country code button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 px-2 py-2 border border-r-0 border-border rounded-l-md bg-muted/50 hover:bg-muted text-sm min-w-[80px] h-9"
        data-testid="phone-country-select"
      >
        <span className="text-base">{selected.flag}</span>
        <span className="text-xs text-muted-foreground">{selected.code}</span>
        <ChevronDown className="h-3 w-3 text-muted-foreground ml-auto" />
      </button>

      {/* Phone number input */}
      <Input
        ref={inputRef}
        type="tel"
        value={local}
        onChange={handleLocalChange}
        placeholder="Phone number"
        className="rounded-l-none border-l-0 flex-1"
        data-testid="lead-phone-input"
      />

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 z-50 w-72 mt-1 bg-popover border border-border rounded-md shadow-lg max-h-60 overflow-hidden">
          <div className="p-2 border-b border-border">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search country..."
              className="w-full px-2 py-1.5 text-sm bg-transparent border border-border rounded outline-none focus:ring-1 focus:ring-ring"
              autoFocus
            />
          </div>
          <div className="overflow-y-auto max-h-48">
            {filtered.map((c, i) => (
              <button
                key={`${c.country}-${i}`}
                type="button"
                onClick={() => handleCountrySelect(c)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent transition-colors ${
                  c.code === selected.code && c.country === selected.country ? 'bg-accent/50' : ''
                }`}
              >
                <span className="text-base">{c.flag}</span>
                <span className="flex-1 text-left">{c.name}</span>
                <span className="text-xs text-muted-foreground">{c.code}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
